import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css, styled } from "@pigment-css/react";
import { clsx } from "clsx";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode } from "@acto/taskpane/core/domain";
import { MagicEdit, DotsHorizontal } from "@acto/web-common/components";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useTestWorkflowQuery,
  useToggleWorkflowStatus,
  useUpdateWorkflow,
  useWorkflowById,
  useWorkflowKpis,
  useWorkflows,
} from "@acto/taskpane/core/data-access/workflow";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { ManagementDataTable } from "../ui/DataTable/ManagementDataTable";
import { ManagementDataTableSkeleton } from "../ui/DataTable/ManagementDataTableSkeleton";
import type { Column, Row } from "../ui/DataTable/types";
import { type KPIHeaderColumn, KPIHeaders } from "../ui/KPIHeaders/KPIHeaders";
import { KPIHeadersSkeleton } from "../ui/KPIHeaders/KPIHeadersSkeleton";
import { PageHeader } from "../ui/PageHeader/PageHeader";
import { ManagementSearchAndFilterBar } from "../ui/SearchAndFilterBar/ManagementSearchAndFilterBar";
import { Flex } from "@acto/taskpane/core/components/flex/Flex";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { useCascadingPeriod } from "../hooks/useCascadingPeriod";
import { ManagementSidepanel } from "../ui/Sidepanel/ManagementSidepanel";
import type { ToggleGroupOption } from "../ui/primitives";
import { Toggle, ToggleGroup } from "../ui/primitives";
import { WorkflowDetailsSidePanel } from "../ui/SidePanelContent/WorkflowDetailsSidePanel/WorkflowDetailsSidePanel";
import { WorkflowDetailsSidePanelSkeleton } from "../ui/SidePanelContent/WorkflowDetailsSidePanel/WorkflowDetailsSidePanelSkeleton";
import { CreateWorkflowSidePanel } from "../ui/SidePanelContent/CreateWorkflowSidePanel";
import type { CreateWorkflowInput, FilterQueryInput, UpdateWorkflowInput } from "@acto/gql/graphql";
import { Conjunction } from "@acto/gql/graphql";
import { DateRange, PeriodOption } from "../ui/DateRangeSelector/types";
import { DateRangeSelector } from "../ui/DateRangeSelector/DateRangeSelector";
import { periodToDateRange } from "../utils/periodToDateRange";
import { Divider, PaddingWrapper } from "../ui/layout";
import { formatComparisonRange } from "../utils/dateFormatters";
import { useSortHandler } from "../hooks/useSortHandler";

const NewWorkflowButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  height: "52px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[80],
  backgroundColor: theme.managementView.colors.neutral[8],
  border: "none",
  borderRadius: "50px",
  padding: `0 ${theme.managementView.spacing[24]}`,
  cursor: "pointer",
  transition: "background-color 0.2s",
  overflow: "hidden",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[16],
  },
  "& svg": {
    width: "16px",
    height: "16px",
    flexShrink: 0,
  },
}));

const TestWorkflowButton = styled("button")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1.25px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  padding: `0 ${theme.managementView.spacing[24]}`,
  height: "40px",
  cursor: "pointer",
  transition: "all 0.2s",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.brand[500]}`,
    outlineOffset: "2px",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

const saveButtonStyles = css(({ theme }) => ({
  backgroundColor: theme.colors.brand[500],
  color: theme.managementView.colors.neutral[0],
  border: "none",
  "&:hover": {
    backgroundColor: theme.colors.brand[600],
  },
}));

const moreMenuButtonStyles = css(({ theme }) => ({
  background: "none",
  border: `1.25px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  width: "48px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  color: theme.managementView.colors.neutral[90],
  outline: "none",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const dropdownContentStyles = css(({ theme }) => ({
  minWidth: "180px",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
  marginTop: theme.managementView.spacing[8],
  overflow: "hidden",
  zIndex: 10010,
}));

const dropdownItemStyles = css(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[80],
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[16]}`,
  borderRadius: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  cursor: "pointer",
  border: "none",
  outline: "none",
  backgroundColor: "transparent",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const PanelErrorText = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[13],
  color: theme.managementView.colors.error[100],
  whiteSpace: "nowrap",
}));

const DEFAULT_FILTER_QUERY: FilterQueryInput = {
  groupConjunction: Conjunction.And,
  groups: [
    {
      conjunction: Conjunction.And,
      conditions: [],
    },
  ],
};

export const WorkflowPage = () => {
  const t = useTranslations("workflows");
  const tKpi = useTranslations("kpi");
  const comparisonMode = useComparisonModeContext();
  const subtitle =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("subtitlePeriodOverPeriod") : t("subtitleYearOverYear");
  const { locale } = useLocale();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [isCreateWorkflowOpen, setIsCreateWorkflowOpen] = useState(false);
  const { sortColumn, sortDirection, handleSort } = useSortHandler("name", "asc");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const {
    displayValue: searchValue,
    setDisplayValue: setSearchValue,
    debouncedValue: debouncedSearchTerm,
  } = useDebouncedSearchTerm();

  const [selectedPeriod, setSelectedPeriod] = useState(PeriodOption.THIS_MONTH);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);

  const [filterQuery, setFilterQuery] = useState<FilterQueryInput>(DEFAULT_FILTER_QUERY);
  const [buttonMode, setButtonMode] = useState<"test" | "save">("test");
  const panelInstanceKeyRef = useRef(0);
  const [panelInstanceKey, setPanelInstanceKey] = useState(0);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [isFormValid, setIsFormValid] = useState(false);

  const workflowSidepanelPeriod = useCascadingPeriod(selectedPeriod, customDateRange, selectedWorkflowId);

  const effectiveDateRange = periodToDateRange(selectedPeriod, customDateRange);

  const { kpis, isFetching: kpisFetching } = useWorkflowKpis({
    dateRange: effectiveDateRange,
  });

  const { workflows, isPending, isFetching, hasNextPage, fetchNextPage } = useWorkflows({
    searchTerm: debouncedSearchTerm || undefined,
    statusFilter,
  });

  const sidepanelDateRange = periodToDateRange(workflowSidepanelPeriod.period, workflowSidepanelPeriod.customDateRange);

  const {
    workflow: selectedWorkflow,
    metrics: selectedWorkflowMetrics,
    activities: selectedWorkflowActivities,
    isPending: isSelectedWorkflowPending,
    isFetching: isSelectedWorkflowFetching,
    refetch: refetchSelectedWorkflow,
  } = useWorkflowById(selectedWorkflowId, sidepanelDateRange);

  const { toggleStatus } = useToggleWorkflowStatus();
  const { createWorkflow, error: createError, reset: resetCreateWorkflow } = useCreateWorkflow();
  const { updateWorkflow, error: updateError, reset: resetUpdateWorkflow } = useUpdateWorkflow();
  const { deleteWorkflow } = useDeleteWorkflow();
  const {
    testQuery,
    result: testResult,
    isPending: isTestPending,
    error: testError,
    reset: resetTestQuery,
  } = useTestWorkflowQuery();
  const track = useTrack();

  useEffect(() => {
    setButtonMode("test");
    resetTestQuery();
    resetCreateWorkflow();
    resetUpdateWorkflow();
  }, [filterQuery, resetTestQuery, resetCreateWorkflow, resetUpdateWorkflow]);

  const handleToggleWorkflow = useCallback(
    async (workflowId: string, currentStatus: boolean) => {
      setTogglingIds((prev) => new Set(prev).add(workflowId));
      try {
        const succeeded = await toggleStatus(workflowId, currentStatus);

        if (succeeded) {
          const workflow = workflows.find((w) => w.id === workflowId);
          const properties = {
            workflowId,
            workflowName: workflow?.name ?? "",
          };
          if (currentStatus) {
            track(AnalyticsEvents.MV_WORKFLOW_DEACTIVATED, properties);
          } else {
            track(AnalyticsEvents.MV_WORKFLOW_ACTIVATED, properties);
          }
        }
      } finally {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(workflowId);
          return next;
        });
      }
    },
    [toggleStatus, workflows, track],
  );

  const computeConditionCount = useCallback(
    (query: FilterQueryInput): number => query.groups.reduce((sum, g) => sum + g.conditions.length, 0),
    [],
  );

  const columns: Column[] = [
    {
      key: "status",
      label: t("columns.status"),
      type: "custom",
      sortable: false,
      width: "112px",
      renderCell: (row: Row) => (
        <Toggle
          checked={row.isActive === "true"}
          onChange={() => handleToggleWorkflow(row.id, row.isActive === "true")}
          loading={togglingIds.has(row.id)}
          ariaLabel={`Toggle ${row.name as string}`}
        />
      ),
    },
    {
      key: "name",
      label: t("columns.name"),
      type: "string",
      sortable: true,
      width: "50%",
    },
    {
      key: "repeat",
      label: t("columns.frequency"),
      type: "string",
      sortable: false,
      width: "25%",
    },
    {
      key: "activeTodosCount",
      label: t("columns.signals"),
      type: "string",
      sortable: true,
      width: "25%",
    },
  ];

  const rows: Row[] = workflows.map((workflow) => ({
    id: workflow.id,
    name: workflow.name,
    isActive: workflow.isActive ? "true" : "false",
    repeat: workflow.repeat ? t("frequency.repeating") : t("frequency.oneTime"),
    activeTodosCount: `${workflow.activeTodosCount} ${t("signals")}`,
  }));

  const comparisonWindowsLabel = useMemo(() => {
    if (!kpis) return null;
    return tKpi("comparisonWindows", {
      current: formatComparisonRange(kpis.dateRanges.current, locale),
      previous: formatComparisonRange(kpis.dateRanges.previous, locale),
    });
  }, [kpis, locale, tKpi]);

  const kpiHeaders: KPIHeaderColumn[] = useMemo(() => {
    if (!kpis) return [];

    const changeOrUndefined = (value: number, change: number | null) =>
      value === 0 || change === null ? undefined : change;

    return [
      {
        title: t("kpiHeaders.todosCreated"),
        value: String(kpis.todosCreated),
        percentageChange: changeOrUndefined(kpis.todosCreated, kpis.todosCreatedChange),
      },
      {
        title: t("kpiHeaders.todosCompleted"),
        value: String(kpis.todosCompleted),
        percentageChange: changeOrUndefined(kpis.todosCompleted, kpis.todosCompletedChange),
      },
      {
        title: t("kpiHeaders.todosDeleted"),
        value: String(kpis.todosDeleted),
        percentageChange: changeOrUndefined(kpis.todosDeleted, kpis.todosDeletedChange),
      },
      {
        title: t("kpiHeaders.overdueTodos"),
        value: String(kpis.overdueTodos),
        percentageChange: changeOrUndefined(kpis.overdueTodos, kpis.overdueTodosChange),
      },
    ];
  }, [kpis, t]);

  const statusFilterOptions: ToggleGroupOption[] = [
    { value: "active", label: t("statusFilter.active") },
    { value: "inactive", label: t("statusFilter.inactive") },
  ];

  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);
  const { workflow: editingWorkflow } = useWorkflowById(editingWorkflowId, effectiveDateRange);
  const saveError = editingWorkflowId ? updateError : createError;

  const handleNewWorkflow = () => {
    setEditingWorkflowId(null);
    setFilterQuery(DEFAULT_FILTER_QUERY);
    setButtonMode("test");
    resetTestQuery();
    resetCreateWorkflow();
    resetUpdateWorkflow();
    panelInstanceKeyRef.current += 1;
    setPanelInstanceKey(panelInstanceKeyRef.current);
    setIsCreateWorkflowOpen(true);
  };

  const handleEditWorkflow = () => {
    if (!selectedWorkflow) return;
    if (selectedWorkflow.isCustomQuery) return;

    setEditingWorkflowId(selectedWorkflow.id);
    setButtonMode("test");
    resetTestQuery();
    resetCreateWorkflow();
    resetUpdateWorkflow();
    panelInstanceKeyRef.current += 1;
    setPanelInstanceKey(panelInstanceKeyRef.current);

    setFilterQuery({
      groupConjunction: selectedWorkflow.query.groupConjunction,
      groups: selectedWorkflow.query.groups.map((group) => ({
        conjunction: group.conjunction,
        conditions: group.conditions.map((condition) => ({
          filter: condition.filter,
          params: condition.params.map((param) => ({
            name: param.name,
            operator: param.operator ?? undefined,
            stringValue: param.stringValue ?? undefined,
            stringValues: param.stringValues ? [...param.stringValues] : undefined,
            numericValue: param.numericValue ?? undefined,
            intValue: param.intValue ?? undefined,
            dateValue: param.dateValue ?? undefined,
            boolValue: param.boolValue ?? undefined,
          })),
        })),
      })),
    });
    setIsCreateWorkflowOpen(true);
  };

  const handleSaveNewWorkflow = async (input: CreateWorkflowInput) => {
    const created = await createWorkflow(input);
    if (!created) return;

    track(AnalyticsEvents.MV_WORKFLOW_CREATED, {
      workflowId: created.id,
      workflowName: input.name,
      isRepeating: Boolean(input.repeat),
      hasFixedResponsibleEmployee: Boolean(input.fixedResponsibleEmployeeId),
      conditionCount: computeConditionCount(input.query),
    });
    await toggleStatus(created.id, false);

    setIsCreateWorkflowOpen(false);
  };

  const handleUpdateWorkflow = async (id: string, input: UpdateWorkflowInput) => {
    const updated = await updateWorkflow(id, input);
    if (!updated) return;

    track(AnalyticsEvents.MV_WORKFLOW_UPDATED, {
      workflowId: updated.id,
      workflowName: updated.name,
      isRepeating: updated.repeat,
      hasFixedResponsibleEmployee: Boolean(updated.fixedResponsibleEmployee),
      conditionCount: input.query
        ? computeConditionCount(input.query)
        : selectedWorkflow
          ? computeConditionCount(selectedWorkflow.query)
          : 0,
    });

    setIsCreateWorkflowOpen(false);
    setEditingWorkflowId(null);
    refetchSelectedWorkflow?.();
  };

  const handleCancelNewWorkflow = () => {
    setIsCreateWorkflowOpen(false);
    setEditingWorkflowId(null);
  };

  const handleDeleteWorkflow = async () => {
    if (!selectedWorkflowId) return;

    const workflowName = selectedWorkflow?.name ?? "";
    const deleted = await deleteWorkflow(selectedWorkflowId);
    if (deleted) {
      track(AnalyticsEvents.MV_WORKFLOW_DELETED, {
        workflowId: selectedWorkflowId,
        workflowName,
      });
      setSelectedWorkflowId(null);
    }
  };

  const handleCloseLevel1 = () => {
    setSelectedWorkflowId(null);
  };

  const handleTestWorkflow = async () => {
    const hasConditions = filterQuery.groups.some((g) => g.conditions.length > 0);
    if (!hasConditions) return;

    const result = await testQuery(filterQuery);
    if (result) setButtonMode("save");
  };

  const actionButtonLabel = useMemo(() => {
    if (buttonMode === "test") {
      if (isTestPending) return `${t("testWorkflow")}...`;
      return t("testWorkflow");
    }

    const count = testResult?.totalCount ?? 0;
    return editingWorkflowId ? t("saveWorkflowWithCount", { count }) : t("createWorkflowWithCount", { count });
  }, [buttonMode, isTestPending, testResult, editingWorkflowId, t]);

  return (
    <>
      <PaddingWrapper>
        <PageHeader
          title={t("title")}
          subtitle={subtitle}
          dateRangeLabel={comparisonWindowsLabel}
          showDateRangeSelector={true}
          selectedPeriod={selectedPeriod}
          customDateRange={customDateRange}
          onPeriodChange={setSelectedPeriod}
          onCustomDateRangeChange={setCustomDateRange}
          actionButton={
            <NewWorkflowButton onClick={handleNewWorkflow}>
              {t("newWorkflow")}
              <MagicEdit />
            </NewWorkflowButton>
          }
        />
      </PaddingWrapper>

      <Flex direction="column" gap={6}>
        <PaddingWrapper direction="column" gap={6}>
          {kpisFetching ? <KPIHeadersSkeleton /> : kpiHeaders.length > 0 && <KPIHeaders columns={kpiHeaders} />}
          {(kpisFetching || kpiHeaders.length > 0) && <Divider />}
          {!isPending && (
            <ManagementSearchAndFilterBar
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              searchPlaceholder={t("searchPlaceholder")}
              filterDropdowns={[]}
              additionalFilters={
                <ToggleGroup
                  options={statusFilterOptions}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as "all" | "active" | "inactive")}
                  ariaLabel={t("columns.status")}
                />
              }
            />
          )}
        </PaddingWrapper>
        {isPending ? (
          <ManagementDataTableSkeleton
            columns={columns}
            rowCount={10}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
        ) : (
          <ManagementDataTable
            columns={columns}
            rows={rows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            hasMore={hasNextPage}
            onLoadMore={fetchNextPage}
            loadMoreLabel={t("loadMore")}
            isLoadingMore={isFetching}
            onRowClick={(rowId) => {
              const isOpening = rowId !== selectedWorkflowId;
              if (isOpening) {
                const workflow = workflows.find((w) => w.id === rowId);
                if (workflow) {
                  track(AnalyticsEvents.MV_WORKFLOW_DETAILS_OPENED, {
                    workflowId: workflow.id,
                    workflowName: workflow.name,
                    isActive: workflow.isActive,
                    activeTodosCount: workflow.activeTodosCount,
                  });
                }
              }
              setSelectedWorkflowId(isOpening ? rowId : null);
              setIsCreateWorkflowOpen(false);
              setEditingWorkflowId(null);
            }}
          />
        )}
      </Flex>
      <ManagementSidepanel
        isOpen={selectedWorkflowId !== null}
        onClose={handleCloseLevel1}
        level={1}
        isLevel1Shrunk={false}
        headerControls={
          <>
            <DateRangeSelector
              selectedPeriod={workflowSidepanelPeriod.period}
              customDateRange={workflowSidepanelPeriod.customDateRange}
              onPeriodChange={workflowSidepanelPeriod.setPeriod}
              onCustomDateRangeChange={workflowSidepanelPeriod.setCustomDateRange}
            />
            <RadixDropdown.Root>
              <RadixDropdown.Trigger className={moreMenuButtonStyles}>
                <DotsHorizontal />
              </RadixDropdown.Trigger>
              <RadixDropdown.Portal>
                <RadixDropdown.Content className={dropdownContentStyles} align="end" sideOffset={4}>
                  <RadixDropdown.Item className={dropdownItemStyles} onSelect={handleDeleteWorkflow}>
                    {t("deleteWorkflow")}
                  </RadixDropdown.Item>
                </RadixDropdown.Content>
              </RadixDropdown.Portal>
            </RadixDropdown.Root>
          </>
        }
      >
        {isSelectedWorkflowPending || isSelectedWorkflowFetching ? (
          <WorkflowDetailsSidePanelSkeleton />
        ) : selectedWorkflow ? (
          <WorkflowDetailsSidePanel
            workflow={selectedWorkflow}
            metrics={selectedWorkflowMetrics}
            activities={selectedWorkflowActivities}
            locale={locale}
            onEdit={handleEditWorkflow}
          />
        ) : null}
      </ManagementSidepanel>
      <ManagementSidepanel
        isOpen={isCreateWorkflowOpen}
        onClose={handleCancelNewWorkflow}
        level={2}
        isLevel1Shrunk={false}
        widePanel={true}
        headerControls={
          <>
            {testError && <PanelErrorText role="alert">{testError.message}</PanelErrorText>}
            {saveError && (
              <PanelErrorText role="alert">{t("saveFailed", { message: saveError.message })}</PanelErrorText>
            )}
            <TestWorkflowButton
              type={buttonMode === "save" ? "submit" : "button"}
              form={buttonMode === "save" ? "create-workflow-form" : undefined}
              onClick={buttonMode === "test" ? handleTestWorkflow : undefined}
              disabled={buttonMode === "test" && (isTestPending || !isFormValid)}
              className={clsx(buttonMode === "save" && saveButtonStyles)}
            >
              {actionButtonLabel}
            </TestWorkflowButton>
          </>
        }
      >
        <CreateWorkflowSidePanel
          key={panelInstanceKey}
          onSave={handleSaveNewWorkflow}
          onUpdate={handleUpdateWorkflow}
          editingWorkflow={editingWorkflowId ? editingWorkflow : undefined}
          filterQuery={filterQuery}
          onFilterQueryChange={setFilterQuery}
          onValidationChange={setIsFormValid}
          onFormChange={() => setButtonMode("test")}
          disabled={isTestPending}
        />
      </ManagementSidepanel>
    </>
  );
};
